import React, { ReactElement, useContext } from 'react';
import { getFormModuleName } from '../../utils/getModuleName';
import styled from 'styled-components';
import ConnectedFormContext from '../Context/ConnectedFormContext';
import { useHistory, useLocation } from 'react-router';
import { p4Tokens } from '../../styles/themes/default/p4FluentTheme';

const Container = styled.div`
  padding: 12px 0 3px 20px;
  background-color: ${p4Tokens.colorHeaderBackground};
`;

const BreadcrumbTitle = styled.div`
  font-size: 13px;
  text-transform: none;
  color: ${p4Tokens.colorBrandMenuForeground5};
  font-weight: 400;
  line-height: 1.1;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const HeaderIcon = styled.span`
  font-size: 14px !important;
  color: #bdbdbd;
  vertical-align: top !important;
  padding-right: 5px;
  cursor: pointer;
  font-family: 'Material Icons', serif;
  font-weight: normal;
  font-style: normal;
  display: inline-block;
  text-transform: none;
  letter-spacing: normal;
  word-wrap: normal;
  white-space: nowrap;
  direction: ltr;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  -moz-osx-font-smoothing: grayscale;
  font-feature-settings: 'liga';
  position: relative;
`;

const backToMenu = (history, path) => {
  if (history && path) {
    if (path === 'back') {
      history.goBack();
    } else {
      history.push({ pathname: path, state: { previousLocation: history.location?.pathname || 'back' } });
    }
  }
};

export const generateCustomBreadcrumb = (
  breadcrumb: Array<{ path: string; title: string }>,
  history: ReturnType<typeof useHistory>,
  workspaceOrigin: boolean
) => {
  const crumbs: Array<ReactElement> = [];
  crumbs.push(
    <HeaderIcon key={'_/'} className="icon" onClick={() => backToMenu(history, '/')}>
      home
    </HeaderIcon>
  );

  for (let i = 0; i < breadcrumb.length; i++) {
    crumbs.push(
      <span key={`_${i}`} onClick={() => backToMenu(history, breadcrumb[i].path)}>
        {breadcrumb[i].title}
        {i !== breadcrumb.length - 1 ? ' / ' : null}
      </span>
    );
  }
  if (workspaceOrigin) {
    return crumbs;
  }
  return <Container>{crumbs}</Container>;
};

export type IBreadCrumbsProps = {
  customBreadcrumb?: Array<{ path: string; title: string }>;
  noBreadCrumb?: boolean;
  shortUrl?: string;
  title: string;
};

const BreadCrumbs = ({ customBreadcrumb, noBreadCrumb, shortUrl, title }: IBreadCrumbsProps) => {
  const connectedForm = useContext(ConnectedFormContext);
  const history = useHistory();
  const location = useLocation();

  if (noBreadCrumb) return null;

  const params = connectedForm?.match?.params;
  const nav = params ? getFormModuleName(location, params) : getFormModuleName(location);
  const module = nav.module;
  const submodule = nav.submodule;
  const subtitle = nav.subtitle;
  const modtitle = nav.modtitle;
  const pathname = location && location.pathname ? location.pathname : '';
  const args: Array<string> = pathname.split('/');

  let breadcrumb;
  if (customBreadcrumb) {
    breadcrumb = generateCustomBreadcrumb(customBreadcrumb, history, false);
  } else {
    let titleSpan;
    if (args.length > 4) {
      titleSpan = <span onClick={() => backToMenu(history, 'back')}> / {title} </span>;
    }

    if (module) {
      breadcrumb = (
        <Container>
          <HeaderIcon className="icon" onClick={() => backToMenu(history, '/')}>
            home
          </HeaderIcon>
          <span onClick={() => backToMenu(history, `/menu/${module}`)}>{modtitle} </span>
          {submodule && !shortUrl ? (
            <span onClick={() => backToMenu(history, `/menu/${module}/${submodule}`)}> / {subtitle}</span>
          ) : null}
          {titleSpan}
        </Container>
      );
    } else {
      breadcrumb = (
        <Container>
          <HeaderIcon className="icon" onClick={() => backToMenu(history, '/')}>
            home
          </HeaderIcon>
          <span onClick={() => backToMenu(history, 'back')}>{title}</span>
        </Container>
      );
    }
  }

  return <BreadcrumbTitle>{breadcrumb}</BreadcrumbTitle>;
};

export default BreadCrumbs;
